<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {addAttributes} from '../../shared/use.add-attributes';
    import {getContext, onDestroy} from 'svelte';
    import {formatToCustomValue, parseFromCustomValue, validatorFactory} from './utils';
    import {Switch, SwitchGroup} from '@rgossiaux/svelte-headlessui';
    import {touched} from '../../shared/use.touched';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {merge, Subject, zip} from 'rxjs';
    import {debounceTime, distinctUntilChanged, filter} from 'rxjs/operators';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {createInputDescription} from '../../description/utils';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {style} from 'svelte-forms';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import Description from '../../description/Description.svelte';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import LabelIdProvider from '../../label/LabelIdProvider.svelte';
    import DescriptionIdProvider from '../../description/DescriptionIdProvider.svelte';
    import type {
        BooleanValueEditorLocalizations,
        BooleanValueEditorOptions,
        BooleanValueEditorValidations
    } from './types';

    export let model: boolean;
    export let formControlOptions: FormControlOptions;

    export let placeholder: string; // boolean editor does not support placeholder

    export let editorId: string;
    export let editorName: string;
    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: BooleanValueEditorOptions;
    export let validations: BooleanValueEditorValidations;

    ignoreUnusedProperties(placeholder);

    model = model ?? false;

    const localize: EditorsLocalizationFunction<BooleanValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const RESET_SYMBOL = Symbol('reset-notification');
    const resetNotification$ = new Subject();
    const blurEvent$ = new Subject<boolean>();
    const inputEvent$ = new Subject<boolean>();
    const addAttributesOptions = new Map([
        ['name', editorName]
    ]);

    let currentViewValue: boolean;
    $: onChange(currentViewValue);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withModifiers([parseFromCustomValue(options), emptyAsNullParser(options)])
        .withFormatters([formatToCustomValue(options), emptyAsNullFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const inputValueStreams = [];
    formControl.formControlOptions.updateTriggers.forEach(({eventName, debounceValue = 0}) => {
        if (eventName === 'input') {
            inputValueStreams.push(inputEvent$.pipe(debounceTime(debounceValue)));
        }

        if (eventName === ' blur') {
            inputValueStreams.push(blurEvent$.pipe(debounceTime(debounceValue)));
        }
    });

    const inputValuesSubscription = merge(...inputValueStreams, resetNotification$)
        .pipe(
            distinctUntilChanged(),
            filter((value) => value !== RESET_SYMBOL),
            filter((value) => value === currentViewValue)) // ignore debounced value if model was changed in the meantime from outside
        .subscribe((value) => formControl.setViewValue(value));

    const viewValueAndErrorsSubscription = zip([formControl.getViewValue$(), formControl.getErrors$()])
        .subscribe(([viewValue]) => renderViewValue(viewValue));

    onDestroy(() => {
        subscription.unsubscribe();
        inputValuesSubscription.unsubscribe();
        viewValueAndErrorsSubscription.unsubscribe();
    });

    export function getFormController(): FormControl<boolean> {
        return formControl;
    }

    function renderViewValue(viewValue: boolean) {
        currentViewValue = viewValue;
        resetNotification$.next(RESET_SYMBOL);
    }

    function onChange(value: boolean) {
        inputEvent$.next(value);
    }

    function onBlur() {
        blurEvent$.next(currentViewValue);
    }
</script>

<div>
    <SwitchGroup class="boolean-value-editor size-{size}">
        <LabelIdProvider/>
        <Switch class="{model ? 'switch switch-enabled toggle-on' : 'switch switch-disabled toggle-off'} {isDisabled ? 'disabled' : ''}"
                bind:checked={currentViewValue}
                disabled={isDisabled}
                aria-required={validations.required}
                on:blur={onBlur}
                use={[
                    [addAttributes, addAttributesOptions],
                    [touched, {formControl}],
                    [focusOnMount, isFocused],
                    [ariaInvalid, formControl],
                    [ariaErrormessage, formControl],
                    [style, {field: formControl.getFieldStateAsStore()}]]}
                data-id={editorId}
                data-main-input>
        </Switch>
        <DescriptionIdProvider {inputDescription}/>
    </SwitchGroup>
    <ErrorMessages customClass="boolean-switch" standalone={true} inline={true} formController={formControl} {forceShowErrors} {size}/>
</div>

<Description {inputDescription}/>

<style lang="less" global>
    @import (reference) "bootstrap-less/bootstrap/variables";
    @import (reference) "bootstrap-less/bootstrap/mixins";

    @switch-size: 1em;
    @switch-border-width: 0.125em;
    @switch-border-inner-radius: calc(@switch-size);
    @switch-border-outer-radius: calc(@switch-border-inner-radius + @switch-border-width);
    @disabled-opacity: 0.5;

    .switch-custom-focus() {
        @color: @input-border-focus;
        @color-rgba: rgba(red(@color), green(@color), blue(@color), .6);
        .box-shadow(~"inset 0 1px 1px rgba(0,0,0,.075), 0 0 6px @{color-rgba}");
    }

    kp-value-editor[data-type="boolean"] {
        .switch {
            all: unset;
            -webkit-appearance: none;
            appearance: none;
            box-sizing: content-box;
            position: relative;
            display: inline-block;
            vertical-align: middle;
            width: calc(@switch-size * 2);
            height: @switch-size;
            border: @switch-border-width solid @brand-primary;
            border-radius: @switch-border-outer-radius;
            transition: border-color ease-in-out .15s, box-shadow ease-in-out .15s, background-color ease .3s;
            cursor: pointer;
        }

        .switch-enabled {
            background-color: lighten(@brand-primary, 10%); // #4d94d1
        }

        .switch-disabled {
            background-color: #e0e0e0;
        }

        .switch.disabled {
            cursor: @cursor-disabled;
            border-color: @gray-light;
            opacity: @disabled-opacity;
        }

        .switch:focus {
            .switch-custom-focus();
        }

        .switch:before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            width: @switch-size;
            height: @switch-size;
            background: @input-bg;
            border-radius: @switch-border-inner-radius;
            transition: background 0.3s, left 0.3s;
        }

        .toggle-on:before {
            left: @switch-size;
        }

        .error-message.boolean-switch {
            left: 5px;
        }

        .boolean-value-editor {
            display: inline-block;

            &.size-xs {
                font-size: 1em;
            }

            &.size-sm {
                font-size: 1.35em;
            }

            &.size-md {
                font-size: 1.7em;
            }
        }
    }

    .has-error {
        kp-value-editor[data-type="boolean"] {
            .switch {
                border-color: @state-danger-text;
                box-shadow: 0 0 2px #ce8483;
                .box-shadow(inset 0 1px 1px rgba(0, 0, 0, .075)); // Redeclare so transitions work

                &:focus {
                    border-color: darken(@state-danger-text, 10%);
                    @shadow: inset 0 1px 1px rgba(0, 0, 0, .075), 0 0 6px lighten(@state-danger-text, 20%);
                    .box-shadow(@shadow);
                }
            }

        }

        /* reset error styles if editor is nested in meta-editor which has error */

        .has-not-error {
            kp-value-editor[data-type="boolean"] {
                .switch {
                    border-color: @brand-primary;
                    box-shadow: none;

                    &:focus {
                        .switch-custom-focus();
                    }
                }
            }
        }
    }
</style>